import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { opsService, type BackupStatus } from '../../services/opsService';

export function InfrastructureBackupsPage() {
  const [bak, setBak] = useState<BackupStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBak(await opsService.backup());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
      setBak(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">بکاپ</h1>
          <p className="mt-1 text-sm text-slate-500">
            عامل فقط وضعیت را گزارش می‌کند و هرگز فایل بکاپ را حذف نمی‌کند.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          تازه‌سازی
        </button>
      </div>

      {!bak && loading ? (
        <div className="rounded-xl bg-white p-8 text-center text-slate-500 shadow-sm">در حال بارگذاری…</div>
      ) : !bak ? (
        <div className="rounded-xl bg-rose-50 p-6 text-rose-800">وضعیت بکاپ در دسترس نیست.</div>
      ) : !bak.available ? (
        <div className="rounded-xl bg-amber-50 p-6 text-amber-900">{bak.unavailableReason || 'نامشخص'}</div>
      ) : (
        <dl className="grid gap-3 rounded-xl bg-white p-5 shadow-sm sm:grid-cols-2">
          <Item label="مسیر" value={bak.dir || '—'} />
          <Item
            label="آخرین بکاپ موفق"
            value={bak.lastSuccessAt ? new Date(bak.lastSuccessAt).toLocaleString('fa-IR') : 'یافت نشد'}
          />
          <Item
            label="سن آخرین بکاپ"
            value={
              bak.lastSuccessAgeSec != null ? `${Math.round(bak.lastSuccessAgeSec / 3600)} ساعت` : '—'
            }
          />
          <Item
            label="حجم آخرین فایل"
            value={bak.lastSuccessSizeMb != null ? `${bak.lastSuccessSizeMb.toFixed(1)} MB` : '—'}
          />
          <Item
            label="حجم پوشه"
            value={bak.dirUsageMb != null ? `${bak.dirUsageMb.toFixed(0)} MB` : '—'}
          />
          <Item
            label="تایمر systemd"
            value={bak.timerActive == null ? 'نامشخص' : bak.timerActive ? 'فعال' : 'غیرفعال'}
          />
          <Item label="نتیجه سرویس" value={bak.serviceLastResult || '—'} />
          <Item label="بازداری" value={bak.retentionHint || '—'} />
        </dl>
      )}
    </div>
  );
}

function Item({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-slate-50 px-4 py-3">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm font-semibold text-slate-900">{value}</dd>
    </div>
  );
}
